import { useState, useEffect, useCallback } from 'react'

import { documentToReactComponents } from '@contentful/rich-text-react-renderer'
import classnames from 'classnames'

import { gaSendEvent } from 'analytics'
import { IconButton } from 'components/IconButton'
import { Select } from 'components/Select'
import { IconCredits, IconLanguage, IconTranscript } from 'components/icons'
import { Modal, ModalBody } from 'layouts/Layout/modal'
import { getLanguage, setLanguage } from 'storage'
import { AudioBlock } from 'types/contentful'
import { useSettings } from 'utils/settings'
import { useTranslation } from 'utils/translation'

import { PlayerDetailsContainer, PageContent } from './styles'

export const PlayerDetails = ({
  id,
  credits,
  transcript,
  availableLanguages,
  small = false,
}: Pick<AudioBlock, 'credits' | 'transcript'> & {
  id?: string
  small?: boolean
  availableLanguages: string[]
}) => {
  const [modalOpen, setModalOpen] = useState<'credits' | 'transcript' | null>(
    null
  )
  const { t } = useTranslation()
  const { settings } = useSettings()
  const storedLanguage = getLanguage()

  const [lang, setLang] = useState<string>(
    storedLanguage && storedLanguage.length > 0 ? storedLanguage : 'en'
  )

  const onLanguageSelect = useCallback((value) => {
    setLang(value)
  }, [])

  useEffect(() => {
    setLanguage(lang)
  }, [lang])

  const options = settings.contentLanguages
    .reduce((acc: string[], curr: string) => {
      if (availableLanguages.some((item) => item === curr))
        return [...acc, curr]
      return acc
    }, [])
    .map((value) => ({
      label: t(`languages.${value}`),
      value,
    }))

  const audioSlug = id ? `${id}/` : ''

  return (
    <>
      <PlayerDetailsContainer small={small}>
        <Select
          label={t('player.language-label')}
          labelAsValueDisplay
          dark
          positionRight={small}
          icon={IconLanguage}
          value={lang}
          onChange={onLanguageSelect}
          options={options}
          menuPlacement="top"
        />
        <IconButton
          title={t('player.credits-title')}
          iconSize="small"
          icon={IconCredits}
          onClick={() => {
            setModalOpen('credits')
            gaSendEvent(`/stories/${audioSlug}${storedLanguage}/credits`)
          }}
        >
          {t('player.credits-button')}
        </IconButton>
        <IconButton
          title={t('player.transcript-title')}
          iconSize="small"
          icon={IconTranscript}
          onClick={() => {
            setModalOpen('transcript')
            gaSendEvent(`/stories/${audioSlug}${storedLanguage}/transcript`)
          }}
        >
          {t('player.transcript-button')}
        </IconButton>
      </PlayerDetailsContainer>
      {modalOpen && (
        <Modal
          title={modalOpen !== null ? modalOpen : ''}
          closeTitle={t(
            modalOpen === 'transcript' ? 'transcript-close' : 'credits-close'
          )}
          onClose={() => {
            setModalOpen(null)
          }}
        >
          <ModalBody
            alignItems={modalOpen === 'transcript' ? 'flex-start' : 'center'}
            direction="column"
          >
            <PageContent className={classnames([lang])}>
              {modalOpen === 'credits' &&
                credits &&
                documentToReactComponents(credits.json)}
              {modalOpen === 'transcript' &&
                transcript &&
                documentToReactComponents(transcript.json)}
            </PageContent>
          </ModalBody>
        </Modal>
      )}
    </>
  )
}
